/**
 * Hope3DCharacterSDK - 3D Character using Convai Web SDK
 *
 * Features:
 * - Uses Convai Web SDK with API authentication (no user login required)
 * - ReadyPlayerMe 3D avatar (swappable with MetaHuman later)
 * - Voice interaction with facial expressions
 * - Fully integrated into MyClinicCore
 */

import React, { useEffect, useRef, useState } from 'react';
// @ts-ignore - SDK may not have full TypeScript definitions
import { ConvaiClient } from 'convai-web-sdk';
import Hope3DAvatarIframe from './Hope3DAvatarIframe';

interface Hope3DCharacterSDKProps {
  patientId: string;
  patientName?: string;
}

export default function Hope3DCharacterSDK({ patientId, patientName }: Hope3DCharacterSDKProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTalking, setIsTalking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visemeData, setVisemeData] = useState<number[] | null>(null);
  const convaiClientRef = useRef<any>(null);
  const avatarRef = useRef<HTMLDivElement>(null);

  // Initialize Convai Client
  useEffect(() => {
    const initializeConvai = async () => {
      try {
        const apiKey = process.env.REACT_APP_CONVAI_API_KEY;
        const characterId = process.env.REACT_APP_CONVAI_CHARACTER_ID;

        if (!apiKey || !characterId) {
          setError('Convai API key or Character ID not configured');
          return;
        }

        // Initialize Convai Client with API authentication
        const client = new ConvaiClient({
          apiKey: apiKey,
          characterId: characterId,
          enableAudio: true,
          enableFacialData: true,
          faceModel: 3, // OVR lipsync model
          sessionId: `patient_${patientId}_${Date.now()}`,
        });

        // Set response callback to capture viseme data
        client.setResponseCallback((response: any) => {
          console.log('Convai Response:', response);

          // Handle facial expressions and animations here
          if (response.hasUserQuery()) {
            console.log('User said:', response.getUserQuery());
          }

          if (response.hasAudioResponse()) {
            console.log('Hope is responding...');

            // Extract viseme data for lip-sync
            const audioResponse = response.getAudioResponse();
            if (audioResponse && audioResponse.getVisemesData) {
              const visemesData = audioResponse.getVisemesData();
              if (visemesData && visemesData.array && visemesData.array[0]) {
                const faceData = visemesData.array[0];
                // Check if valid viseme data (not -2 which means no data)
                if (faceData[0] !== -2) {
                  setVisemeData(faceData);
                  console.log('Viseme data received:', faceData);
                }
              }
            }
          }
        });

        // Set audio play callback - when Hope starts speaking
        client.onAudioPlay(() => {
          console.log('Hope started speaking');
          setIsTalking(true);
        });

        // Set audio stop callback - when Hope stops speaking
        client.onAudioStop(() => {
          console.log('Hope stopped speaking');
          setIsTalking(false);
          setVisemeData(null); // Clear viseme data when done talking
        });

        convaiClientRef.current = client;
        setIsInitialized(true);
        console.log('Convai SDK initialized successfully!');
      } catch (err) {
        console.error('Error initializing Convai:', err);
        setError('Failed to initialize Hope AI');
      }
    };

    initializeConvai();

    // Cleanup - ConvaiClient doesn't have a close() method
    // The client will be garbage collected when component unmounts
    return () => {
      if (convaiClientRef.current) {
        // Optionally reset session before unmount
        try {
          convaiClientRef.current.resetSession();
        } catch (err) {
          console.log('Session reset on cleanup:', err);
        }
      }
    };
  }, [patientId]);

  // Start voice interaction
  const startConversation = async () => {
    if (!convaiClientRef.current) return;

    try {
      setIsListening(true);
      await convaiClientRef.current.startAudioChunk();
      console.log('Started listening...');
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError('Could not start microphone');
      setIsListening(false);
    }
  };

  // Stop voice interaction
  const stopConversation = () => {
    if (!convaiClientRef.current) return;

    try {
      convaiClientRef.current.endAudioChunk();
      setIsListening(false);
      console.log('Stopped listening');
    } catch (err) {
      console.error('Error stopping conversation:', err);
    }
  };

  return (
    <div style={styles.container}>
      {/* 3D Avatar — full panel, no controls overlay */}
      <div ref={avatarRef} style={styles.avatarContainer}>
        <Hope3DAvatarIframe
          isTalking={isTalking}
          isListening={isListening}
          visemeData={visemeData}
        />
      </div>
    </div>
  );
}

// Styles
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  avatarContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
};
